import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Check, X, Upload, Image as ImageIcon, Link2, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { usePublicSettings, patchPublicSettings } from '../hooks/usePublicSettings';
import { toast } from 'sonner';

/**
 * Returns true if the current user has an admin session AND edit mode is on.
 * Edit mode is toggled via <AdminEditBar />.
 */
export function useEditMode() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const check = () => {
      const hasToken = !!localStorage.getItem('yekuv_token');
      const editOn = localStorage.getItem('yekuv_edit_mode') === '1';
      setOn(hasToken && editOn);
    };
    check();
    const int = setInterval(check, 300); // cheap poll — reacts to AdminEditBar toggle
    window.addEventListener('storage', check);
    return () => { clearInterval(int); window.removeEventListener('storage', check); };
  }, []);
  return on;
}


const _BULLET_RULES = [
  ['-> ', '→ '], ['->', '→ '],
  ['* ', '• '], ['*', '• '],
  ['- ', '• '],
  ['. ', '• '],
];
/** Turn leading markers (*, -, ., ->) of each line into pretty bullets (•, →). */
function decorateBullets(text) {
  return String(text || '').split('\n').map(line => {
    const t = line.trimStart();
    const lead = line.slice(0, line.length - t.length);
    for (const [mark, rep] of _BULLET_RULES) {
      if (t.startsWith(mark)) return lead + rep + t.slice(mark.length);
    }
    return line;
  }).join('\n');
}

/**
 * Click-to-edit inline text bound to a setting key.
 *
 * Props:
 *  - settingKey: string — the settings DB key
 *  - fallback: string — value to show when setting is empty
 *  - as: html tag name (default 'span')
 *  - html: boolean — if true, saves raw HTML and renders via dangerouslySetInnerHTML
 *  - multiline: boolean — uses <textarea> instead of single-line input in edit mode
 *  - className, style — forwarded to the rendered tag
 */
export function InlineText({
  settingKey, fallback = '', as = 'span', html = false, multiline = false,
  className = '', style, children,
}) {
  const s = usePublicSettings();
  const canEdit = useEditMode();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const currentValue = s[settingKey] ?? fallback ?? '';
  const displayValue = (multiline && !html) ? decorateBullets(currentValue) : currentValue;

  const startEdit = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    setDraft(currentValue);
    setEditing(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        if (inputRef.current.setSelectionRange) {
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      }
    }, 30);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    if (draft === currentValue) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.adminUpsertSetting(settingKey, draft);
      patchPublicSettings({ [settingKey]: draft });
      toast.success('Kaydedildi');
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    if (e.key === 'Enter' && !e.shiftKey && !multiline) { e.preventDefault(); save(); }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
  };

  const Tag = as;
  const displayStyle = multiline ? { whiteSpace: 'pre-line', ...style } : style;

  if (!canEdit) {
    // Public render — no wrapper overhead
    if (html) {
      return <Tag className={className} style={displayStyle} dangerouslySetInnerHTML={{ __html: currentValue }} />;
    }
    return <Tag className={className} style={displayStyle}>{displayValue || children}</Tag>;
  }

  if (editing) {
    return (
      <span className="inline-edit-shell" onClick={(e) => e.stopPropagation()}>
        {multiline ? (
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            className={`inline-edit-textarea ${className}`}
            rows={Math.min(10, Math.max(2, draft.split('\n').length + 1))}
          />
        ) : (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            className={`inline-edit-input ${className}`}
            style={style}
          />
        )}
        <span className="inline-edit-actions">
          <button onClick={save} disabled={saving} title="Kaydet (Enter)" className="inline-edit-btn inline-edit-btn-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </button>
          <button onClick={cancel} title="Vazgeç (Esc)" className="inline-edit-btn inline-edit-btn-cancel">
            <X className="w-3.5 h-3.5" />
          </button>
          {html && (
            <span className="inline-edit-hint">HTML: &lt;em&gt;, &lt;br /&gt;, &lt;strong&gt; kullanabilirsiniz</span>
          )}
          {!html && !multiline && (
            <span className="inline-edit-hint">Enter: kaydet · Esc: iptal</span>
          )}
          {multiline && (
            <span className="inline-edit-hint">Ctrl+Enter: kaydet · Esc: iptal</span>
          )}
        </span>
      </span>
    );
  }

  // Not editing but admin — clickable
  const commonProps = {
    className: `inline-edit-target ${className}`,
    style: displayStyle,
    onClick: startEdit,
    'data-testid': `inline-edit-${settingKey}`,
    title: 'Düzenlemek için tıklayın',
  };
  if (html) {
    return <Tag {...commonProps} dangerouslySetInnerHTML={{ __html: currentValue || fallback }} />;
  }
  return <Tag {...commonProps}>{displayValue || children || fallback}</Tag>;
}


/**
 * Click-to-edit inline image bound to a setting key.
 * On click (admin edit mode) shows an inline dialog with:
 *  - "URL ile değiştir"
 *  - "Bilgisayarımdan yükle"
 *
 * Props:
 *  - settingKey: string
 *  - fallback: string (default image URL)
 *  - alt, className, style — passed to <img>
 */
export function InlineImage({ settingKey, fallback = '', alt = '', className = '', style, imgProps = {} }) {
  const s = usePublicSettings();
  const canEdit = useEditMode();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftUrl, setDraftUrl] = useState('');
  const fileRef = useRef(null);
  const src = s[settingKey] || fallback;

  if (!canEdit) {
    return <img src={src} alt={alt} className={className} style={style} {...imgProps} />;
  }

  const openEditor = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraftUrl(src || '');
    setEditing(true);
  };
  const commit = async (newUrl) => {
    if (!newUrl) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.adminUpsertSetting(settingKey, newUrl);
      patchPublicSettings({ [settingKey]: newUrl });
      toast.success('Görsel güncellendi');
      setEditing(false);
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setSaving(true);
    try {
      const r = await api.adminUploadFile(f);
      await commit(r.url);
    } catch { toast.error('Yüklenemedi'); setSaving(false); }
  };

  return (
    <span className="inline-edit-image-shell" onClick={(e) => canEdit && !editing && e.stopPropagation()}>
      <img
        src={src}
        alt={alt}
        className={`${className} inline-edit-image`}
        style={style}
        onClick={openEditor}
        {...imgProps}
      />
      {editing && (
        <span className="inline-edit-image-popover" onClick={(e) => e.stopPropagation()}>
          <span className="inline-edit-image-header">
            <ImageIcon className="w-4 h-4" /> Görseli Değiştir
            <button onClick={() => setEditing(false)} className="ml-auto inline-edit-btn inline-edit-btn-cancel"><X className="w-3 h-3" /></button>
          </span>
          <label className="inline-edit-image-label"><Link2 className="w-3.5 h-3.5" /> URL</label>
          <input
            value={draftUrl}
            onChange={(e) => setDraftUrl(e.target.value)}
            placeholder="/uploads/foo.jpg veya https://..."
            className="inline-edit-input"
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
          <span className="inline-edit-image-buttons">
            <button onClick={() => commit(draftUrl)} disabled={saving} className="inline-edit-btn inline-edit-btn-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} URL Uygula
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={saving} className="inline-edit-btn inline-edit-btn-upload">
              <Upload className="w-3.5 h-3.5" /> Yükle
            </button>
          </span>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
        </span>
      )}
      {canEdit && !editing && (
        <span className="inline-edit-image-hint"><Pencil className="w-3 h-3" /> Görsele tıkla</span>
      )}
    </span>
  );
}
